import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Search,
  Filter,
  MessageSquare,
  Calendar,
  Building2,
  User,
  Mail,
  Phone,
  Clock,
  CheckCircle,
  AlertTriangle,
  Eye,
  Archive,
  MoreHorizontal
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { supabase } from '@/integrations/supabase/client';
import { Link } from 'react-router-dom';

interface AdminSubmission {
  id: string;
  form_type: string;
  form_data: Record<string, any>;
  status: 'new' | 'urgent' | 'in_progress' | 'done';
  submitted_at: string;
  updated_at: string;
  user_name?: string;
  user_email?: string;
  camping_site?: string;
  message?: string;
}

const AdminMessages = () => {
  const [submissions, setSubmissions] = useState<AdminSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [campingFilter, setCampingFilter] = useState('all');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  useEffect(() => {
    loadSubmissions();
  }, []);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('admin_submissions_with_history')
        .select('*')
        .order('submitted_at', { ascending: false });
      
      if (error) {
        console.error('Erreur chargement soumissions:', error);
        return;
      }
      
      setSubmissions(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des soumissions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrage des soumissions
  const filteredSubmissions = useMemo(() => {
    return submissions.filter(submission => {
      // Filtre par recherche
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const matchesName = submission.user_name?.toLowerCase().includes(searchLower) ||
                           submission.form_data?.name?.toLowerCase().includes(searchLower) ||
                           submission.form_data?.nom?.toLowerCase().includes(searchLower);
        const matchesEmail = submission.user_email?.toLowerCase().includes(searchLower) ||
                            submission.form_data?.email?.toLowerCase().includes(searchLower);
        const matchesMessage = submission.message?.toLowerCase().includes(searchLower) ||
                              submission.form_data?.message?.toLowerCase().includes(searchLower) ||
                              submission.form_data?.description?.toLowerCase().includes(searchLower);
        
        if (!matchesName && !matchesEmail && !matchesMessage) {
          return false;
        }
      }

      // Filtre par type
      if (typeFilter !== 'all' && submission.form_type !== typeFilter) {
        return false;
      }

      // Filtre par statut
      if (statusFilter !== 'all' && submission.status !== statusFilter) {
        return false;
      }

      // Filtre par camping
      if (campingFilter !== 'all') {
        const camping = submission.camping_site || submission.form_data?.camping || submission.form_data?.site;
        if (camping !== campingFilter) {
          return false;
        }
      }

      return true;
    });
  }, [submissions, searchQuery, typeFilter, statusFilter, campingFilter]);

  // Obtenir la liste des campings uniques
  const campings = useMemo(() => {
    const campingSet = new Set<string>();
    submissions.forEach(s => {
      const camping = s.camping_site || s.form_data?.camping || s.form_data?.site;
      if (camping) {
        campingSet.add(camping);
      }
    });
    return Array.from(campingSet).sort();
  }, [submissions]);

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      technical: 'Technique',
      leave: 'Congés',
      professional: 'Professionnel',
      delivery: 'Livraison',
      seasonal: 'Saisonnier',
      group: 'Groupe',
      bike: 'Vélo',
      reservation: 'Réservation',
      contact: 'Contact',
      partner: 'Partenaire',
      other: 'Autre'
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'urgent':
        return <Badge className="bg-red-500 hover:bg-red-600">Urgent</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-500 hover:bg-blue-600">En cours</Badge>;
      case 'done':
        return <Badge className="bg-green-500 hover:bg-green-600">Terminé</Badge>;
      default:
        return <Badge variant="secondary">Nouveau</Badge>;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'urgent':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'in_progress':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'done':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      default:
        return <MessageSquare className="h-4 w-4 text-gray-500" />;
    }
  };

  const handleBulkAction = async (action: string) => {
    if (selectedItems.length === 0) return;

    try {
      // Ici vous pouvez implémenter les actions en lot
      console.log(`Action ${action} sur les éléments:`, selectedItems);
      
      if (action === 'mark_completed') {
        // Marquer comme terminé
        for (const id of selectedItems) {
          const { error } = await supabase
            .from('admin_submissions_2026_02_13_15_00')
            .update({ status: 'done' })
            .eq('id', id);
          
          if (error) {
            console.error('Erreur mise à jour statut:', error);
          }
        }
        await loadSubmissions();
        setSelectedItems([]);
      }
    } catch (error) {
      console.error('Erreur lors de l\'action en lot:', error);
    }
  };

  const toggleSelection = (id: string) => {
    setSelectedItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selectedItems.length === filteredSubmissions.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredSubmissions.map(s => s.id));
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 min-h-0">
        <Card>
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              <div className="h-10 bg-gray-200 rounded"></div>
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-16 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 min-h-0">
      {/* Filtres et recherche */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Messages & Demandes
            <Badge variant="secondary" className="ml-2">
              {filteredSubmissions.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Barre de recherche */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Rechercher par nom, email ou message..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Filtres */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Type de demande" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="technical">Technique</SelectItem>
                <SelectItem value="leave">Congés</SelectItem>
                <SelectItem value="professional">Professionnel</SelectItem>
                <SelectItem value="delivery">Livraison</SelectItem>
                <SelectItem value="seasonal">Saisonnier</SelectItem>
                <SelectItem value="group">Groupe</SelectItem>
                <SelectItem value="bike">Vélo</SelectItem>
                <SelectItem value="reservation">Réservation</SelectItem>
                <SelectItem value="contact">Contact</SelectItem>
                <SelectItem value="partner">Partenaire</SelectItem>
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="new">Nouveau</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="in_progress">En cours</SelectItem>
                <SelectItem value="done">Terminé</SelectItem>
              </SelectContent>
            </Select>

            <Select value={campingFilter} onValueChange={setCampingFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Camping" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les campings</SelectItem>
                {campings.map(camping => (
                  <SelectItem key={camping} value={camping}>
                    {camping}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={selectAll}
                className="flex-1"
              >
                {selectedItems.length === filteredSubmissions.length ? 'Désélectionner' : 'Tout sélectionner'}
              </Button>
              
              {selectedItems.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      Actions ({selectedItems.length})
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={() => handleBulkAction('mark_completed')}>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Marquer comme terminé
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleBulkAction('archive')}>
                      <Archive className="h-4 w-4 mr-2" />
                      Archiver
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Liste des messages */}
      <Card>
        <CardContent className="p-0">
          <div className="divide-y divide-gray-200">
            {filteredSubmissions.map((submission) => (
              <div
                key={submission.id}
                className={`p-6 hover:bg-gray-50 transition-colors ${
                  selectedItems.includes(submission.id) ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-start gap-4">
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(submission.id)}
                    onChange={() => toggleSelection(submission.id)}
                    className="mt-1"
                  />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusIcon(submission.status)}
                      <Badge variant="outline" className="text-xs">
                        {getTypeLabel(submission.form_type)}
                      </Badge>
                      {getStatusBadge(submission.status)}
                      {(submission.camping_site || submission.form_data?.camping) && (
                        <Badge variant="secondary" className="text-xs">
                          <Building2 className="h-3 w-3 mr-1" />
                          {submission.camping_site || submission.form_data?.camping}
                        </Badge>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="font-medium">
                          {submission.user_name || submission.form_data?.name || submission.form_data?.nom || 'Anonyme'}
                        </span>
                      </div>
                      
                      {(submission.user_email || submission.form_data?.email) && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {submission.user_email || submission.form_data?.email}
                          </span>
                        </div>
                      )}
                      
                      {submission.form_data?.telephone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {submission.form_data.telephone}
                          </span>
                        </div>
                      )}
                    </div>

                    <p className="text-sm text-gray-700 mb-3 line-clamp-2">
                      {submission.message || submission.form_data?.message || submission.form_data?.description || 'Pas de message'}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Calendar className="h-3 w-3" />
                        {new Date(submission.submitted_at).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          asChild
                          variant="outline"
                          size="sm"
                        >
                          <Link to={`/admin/messages/${submission.id}`}>
                            <Eye className="h-4 w-4 mr-2" />
                            Voir détail
                          </Link>
                        </Button>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Marquer terminé
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <AlertTriangle className="h-4 w-4 mr-2" />
                              Marquer urgent
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Archive className="h-4 w-4 mr-2" />
                              Archiver
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredSubmissions.length === 0 && (
            <div className="p-12 text-center">
              <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Aucun message trouvé
              </h3>
              <p className="text-gray-500">
                Aucun message ne correspond aux filtres sélectionnés.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminMessages;